import { useCallback, useMemo } from 'react';
import {
  useCostCentersLazyQuery,
  useDepartmentsLazyQuery,
  useDivisionsLazyQuery,
  useEmployeeTypesLazyQuery,
  useLocationsLazyQuery,
  useServiceCentersLazyQuery,
  CostCentersQuery,
  DepartmentsQuery,
  DivisionsQuery,
  EmployeeTypesQuery,
  LocationsQuery,
  ServiceCentersQuery,
  GroupType
} from 'graphql/types';
import { GroupSelectCount } from '../constant';
export type groupSelectLabelParams = {
  context: string;
  query?: string;
};

export type groupSelectLabelSearchType = {
  data: {
    locations: LocationsQuery | undefined;
    departments: DepartmentsQuery | undefined;
    employeeTypes: EmployeeTypesQuery | undefined;
    costCenters: CostCentersQuery | undefined;
    divisions: DivisionsQuery | undefined;
    serviceCenters: ServiceCentersQuery | undefined;
  };
  loading: {
    locations: boolean;
    departments: boolean;
    employeeTypes: boolean;
    costCenters: boolean;
    divisions: boolean;
    serviceCenters: boolean;
  };
  searchHandler: (searchParams: groupSelectLabelParams) => void;
};

export const useGroupSelectLabel = (): groupSelectLabelSearchType => {
  const [
    getLocations,
    { data: locationsData, loading: locationsLoading }
  ] = useLocationsLazyQuery({ fetchPolicy: 'no-cache' });
  const [
    getCostCenters,
    { data: costCentersData, loading: costCentersLoading }
  ] = useCostCentersLazyQuery({ fetchPolicy: 'no-cache' });
  const [
    getDepartments,
    { data: departmentsData, loading: departmentsLoading }
  ] = useDepartmentsLazyQuery({ fetchPolicy: 'no-cache' });
  const [
    getDivisions,
    { data: divisionsData, loading: divisionsLoading }
  ] = useDivisionsLazyQuery({ fetchPolicy: 'no-cache' });
  const [
    getEmployeeTypes,
    { data: employeeTypesData, loading: employeeTypesLoading }
  ] = useEmployeeTypesLazyQuery({ fetchPolicy: 'no-cache' });
  const [
    getServiceCenters,
    { data: serviceCentersData, loading: serviceCentersLoading }
  ] = useServiceCentersLazyQuery({ fetchPolicy: 'no-cache' });

  const searchHandler = useCallback(
    (searchParams: groupSelectLabelParams) => {
      const variables = {
        input: {
          count: GroupSelectCount,
          query: searchParams.query
        }
      };
      if (searchParams.context === GroupType.Location)
        getLocations({ variables });
      else if (searchParams.context === GroupType.EmployeeType)
        getEmployeeTypes({ variables });
      else if (searchParams.context === GroupType.Department)
        getDepartments({ variables });
      else if (searchParams.context === GroupType.CostCenter)
        getCostCenters({ variables });
      else if (searchParams.context === GroupType.Division)
        getDivisions({ variables });
      else if (searchParams.context === GroupType.ServiceCenter)
        getServiceCenters({ variables });
    },
    [
      getCostCenters,
      getDepartments,
      getDivisions,
      getEmployeeTypes,
      getLocations,
      getServiceCenters
    ]
  );

  return useMemo(
    () => ({
      searchHandler,
      loading: {
        locations: locationsLoading,
        costCenters: costCentersLoading,
        departments: departmentsLoading,
        divisions: divisionsLoading,
        employeeTypes: employeeTypesLoading,
        serviceCenters: serviceCentersLoading
      },
      data: {
        locations: locationsData,
        costCenters: costCentersData,
        departments: departmentsData,
        divisions: divisionsData,
        employeeTypes: employeeTypesData,
        serviceCenters: serviceCentersData
      }
    }),
    [
      costCentersData,
      costCentersLoading,
      departmentsData,
      departmentsLoading,
      divisionsData,
      divisionsLoading,
      employeeTypesData,
      employeeTypesLoading,
      locationsData,
      locationsLoading,
      searchHandler,
      serviceCentersData,
      serviceCentersLoading
    ]
  );
};
